import { Orientation } from '@/modules'
import { ContainersNames } from '@/containers/ContainersNames'
import Facade from '../../Facade'
import { ApplicationResizer } from './ApplicationResizer'
import * as PIXI from 'pixi.js'

export class ApplicationMobileResizer extends ApplicationResizer {
    protected detectOrientation(): Orientation {
        const viewportWidth = window.visualViewport?.width
        const viewportHeight = window.visualViewport?.height

        const width =
            typeof viewportWidth === 'number' && viewportWidth > 0
                ? viewportWidth
                : this._viewWrapper.clientWidth || window.innerWidth

        const height =
            typeof viewportHeight === 'number' && viewportHeight > 0
                ? viewportHeight
                : this._viewWrapper.clientHeight || window.innerHeight

        return width >= height ? Orientation.LANDSCAPE : Orientation.PORTRAIT
    }

    protected updateGameStageBounds(): void {
        if (!Facade.displayConfig) return
        const { WORLD_WIDTH, WORLD_HEIGHT } = Facade.displayConfig.sizes[this._orientation]

        this._gameStageBounds.height = WORLD_HEIGHT
        this._gameStageBounds.width = WORLD_WIDTH
    }

    public updateWrapperBounds(): void {
        const wrapperWidth = this._viewWrapper.clientWidth
        const wrapperHeight = this._viewWrapper.clientHeight
        const viewportHeight = window.visualViewport?.height

        this._wrapperBounds.width = wrapperWidth
        this._wrapperBounds.height =
            typeof viewportHeight === 'number' && viewportHeight > 0
                ? Math.min(wrapperHeight, viewportHeight)
                : wrapperHeight
    }

    public updateCanvasSize(): void {
        const { canvasWidth, canvasHeight } = this
        const { width, height } = this._gameStageBounds

        // UI: contain. Background: cover into the letterbox/pillarbox gaps (landscape + portrait).
        const containScale = Math.min(canvasWidth / width, canvasHeight / height)
        this.applyFullViewportWithBackgroundCover(canvasWidth, canvasHeight, width, height, containScale)

        if (Facade.container) {
            if (this._orientation === Orientation.LANDSCAPE) {
                Facade.container.position.set(983.5, 442)
                Facade.container.scale.set(1)
            } else {
                Facade.container.position.set(483.5, 1067.5)
                Facade.container.scale.set(0.91)
            }
        }
    }

    /**
     * Full-viewport canvas. UI stays contain-fitted & centred;
     * only BACKGROUND_CONTAINER is cover-scaled so the sky fills the gaps.
     */
    private applyFullViewportWithBackgroundCover(
        viewportWidth: number,
        viewportHeight: number,
        worldWidth: number,
        worldHeight: number,
        containScale: number
    ): void {
        const coverScale = Math.max(viewportWidth / worldWidth, viewportHeight / worldHeight)

        Facade.app.stage.scale.set(containScale)
        Facade.app.stage.position.set(
            Math.round((viewportWidth - worldWidth * containScale) / 2),
            Math.round((viewportHeight - worldHeight * containScale) / 2)
        )

        if (Facade.app?.renderer) {
            Facade.app.renderer.resize(viewportWidth, viewportHeight)
        }

        this._canvas.style.width = `${viewportWidth}px`
        this._canvas.style.height = `${viewportHeight}px`
        this._canvas.style.left = '0px'
        this._canvas.style.top = '0px'

        this.applyBackgroundCoverRelativeScale(coverScale / containScale, worldWidth, worldHeight)
    }

    private applyBackgroundCoverRelativeScale(relativeScale: number, worldWidth: number, worldHeight: number): void {
        const background = this.findBackgroundContainer()
        if (!background) {
            return
        }

        // Stage already applies containScale; boost bg so effective scale is cover, centred on world.
        background.scale.set(relativeScale)
        background.position.set((worldWidth / 2) * (1 - relativeScale), (worldHeight / 2) * (1 - relativeScale))
    }

    private findBackgroundContainer(): PIXI.Container | null {
        const stage = Facade.stage || Facade.app?.stage
        if (!stage?.children?.length) {
            return null
        }

        const match = stage.children.find((child: any) => {
            return (
                child?.name === ContainersNames.BACKGROUND_CONTAINER ||
                child?.label === ContainersNames.BACKGROUND_CONTAINER
            )
        })

        return (match as PIXI.Container) || null
    }

    public get canvasHeight(): number {
        return this._wrapperBounds.height
    }

    public get canvasWidth(): number {
        return this._wrapperBounds.width
    }

    public get resolution(): number {
        return window.devicePixelRatio
    }
}
